import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { buildAssignmentOnlyPayload } from '../../utils/ticketAssignmentEdit';
import { trackIncidentOperation } from '../../utils/appInsights';
import { useSlaEvents } from '../../hooks/useSlaEvents';
import { useServiceNowLinkRefetchInterval } from '../../hooks/useServiceNowLinkRefetchInterval';
import SearchableUserDropdown from '../../components/SearchableUserDropdown';
import AccessLostModal from '../../components/AccessLostModal';
import AssignmentGroupChangeConfirmModal from '../../components/AssignmentGroupChangeConfirmModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import SurveyConfirmationModal from '../../components/SurveyConfirmationModal';
import OnHoldReasonModal, { HoldReasonOption } from '../../components/OnHoldReasonModal';
import ResolutionNotesModal from '../../components/ResolutionNotesModal';
import CancellationReasonModal from '../../components/CancellationReasonModal';
import ReopenReasonModal from '../../components/ReopenReasonModal';
import ClosureNotesModal from '../../components/ClosureNotesModal';
import BreachReasonModal from '../../components/BreachReasonModal';
import AttachmentTab from '../../components/AttachmentTab';
import ActivityTab from '../../components/ActivityTab';
import RelatedRecordsTab from '../../components/RelatedRecordsTab';
import TabLoadingSpinner from '../../components/TabLoadingSpinner';
import DuplicateIncidentModal from '../../components/DuplicateIncidentModal';
import CreateProblemFromIncidentModal from '../../components/CreateProblemFromIncidentModal';
import RecordTimeModal from '../../components/RecordTimeModal';
import RichTextEditor from '../../components/RichTextEditor';
import RichTextDisplay from '../../components/RichTextDisplay';
import ResizableTextDisplay from '../../components/ResizableTextDisplay';
import TicketAccessDenied from '../../components/TicketAccessDenied';
import { stripHtml, sanitizeNoteInput } from '../../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import SlaInstancesDisplay, { areAllSlaInstancesTerminal } from '../../components/SlaInstancesDisplay';
import ServiceNowKnownField, { getExternalSystemDisplayName } from '../../components/ServiceNowKnownField';
import {
  priorityLabels,
  caseStateLabels,
  getDisplayLabel
} from '../../utils/labelMappings';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { Menu, Combobox, Transition, Dialog } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useDateFormatter } from '../../hooks/useDateFormatter';

const EMPTY_HOLD_REASON_OPTIONS: HoldReasonOption[] = [];

interface WorkNote {
  id: string;
  User?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  note: string;
  timestamp: string;
  isPublic: boolean;
}

// (Removed Incident interface; fields are inferred from API responses)

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  // Legacy support
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
  NORMAL: 'bg-gray-100 text-gray-800 border-gray-200'
};


const workflowSteps = [
  { id: 'new', name: 'New', icon: ClockIcon },
  { id: 'in_progress', name: 'In Progress', icon: UserIcon },
  { id: 'resolved', name: 'Resolved', icon: CheckIcon },
  { id: 'closed', name: 'Closed', icon: XMarkIcon }
];

const IncidentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const incidentsListSearch = (location.state as { incidentsListSearch?: string } | null)?.incidentsListSearch;

  const navigateBackToIncidents = () => {
    if (incidentsListSearch !== undefined) {
      navigate(`/incidents${incidentsListSearch}`);
    } else {
      navigate('/incidents');
    }
  };
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'notes' | 'attachments' | 'activities' | 'related' | 'closure'>('details');

  // Type definition for incident update payload
  type IncidentUpdatePayload = {
    shortDescription?: string;
    description?: string;
    priority?: string;
    impact?: string;
    urgency?: string;
    state?: string;
    category?: string;
    /** @deprecated Prefer resolutionNotes — kept for legacy problem-style field */
    resolution?: string | null;
    resolutionNotes?: string | null;
    resolutionCode?: string;
    contactId?: string | null;
    assignedTo?: string | null;
    assignedToId?: string | null;
    assignmentGroupId?: string | null;
    parentId?: string | null;
    customerId?: string | null;
    engagementId?: string | null;
    servicePlanId?: string | null;
    resourceId?: string | null;
    channel?: string;
    onHoldReason?: string | null;
    cancellationReason?: string | null;
    reopenReason?: string | null;
    closureNotes?: string | null;
  };

  /** Resolve notes are stored on resolutionNotes; fall back to legacy resolution. */
  const getIncidentResolutionNotes = (inc: { resolutionNotes?: string | null; resolution?: string | null } | null | undefined) =>
    (inc?.resolutionNotes && String(inc.resolutionNotes).trim()) ||
    (inc?.resolution && String(inc.resolution).trim()) ||
    '';

  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'low_4'; // Default to Low

    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);

    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

    return 'low_4'; // Default to Low
  };
  const [editForm, setEditForm] = useState<IncidentUpdatePayload>({});
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'work_note' | 'customer_note'>('work_note');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previousEngagementId, setPreviousEngagementId] = useState<string | null>(null);
  const [previousCustomerId, setPreviousCustomerId] = useState<string | null>(null);

  // Combobox query states for edit form
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');
  const [channelQuery, setChannelQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');

  // Dropdown options
  const impactOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const urgencyOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const priorityOptions = [
    { value: 'critical_1', label: '1 - Critical' },
    { value: 'high_2', label: '2 - High' },
    { value: 'moderate_3', label: '3 - Moderate' },
    { value: 'low_4', label: '4 - Low' },
    { value: 'planning_5', label: '5 - Planning' }
  ];

  const channelOptions = [
    { value: 'web', label: 'Web' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'alert', label: 'Alert' }
  ];

  const categoryOptions = [
    { value: 'question', label: 'Question' },
    { value: 'issue', label: 'Issue' },
    { value: 'request', label: 'Request' },
    { value: 'change', label: 'Change' }
  ];

  // Duplicate and Create Problem modal states
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [createProblemModalOpen, setCreateProblemModalOpen] = useState(false);

  // Record Time modal state
  const [recordTimeModalOpen, setRecordTimeModalOpen] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    targetState: string;
    confirmLabel: string;
    confirmButtonClass: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    targetState: '',
    confirmLabel: 'Confirm',
    confirmButtonClass: 'bg-blue-600 hover:bg-blue-700 text-white'
  });

  // On-hold reason modal state
  const [onHoldModal, setOnHoldModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Resolution notes modal state
  const [resolutionModal, setResolutionModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Cancellation reason modal state
  const [cancellationModal, setCancellationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Reopen reason modal state
  const [reopenModal, setReopenModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Closure notes modal state
  const [closureModal, setClosureModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Survey confirmation modal state (for moderate/low priority)
  const [surveyConfirmModal, setSurveyConfirmModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  // Survey result modal state (for success/error messages)
  const [surveyResultModal, setSurveyResultModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    isOpen: false,
    type: 'success',
    message: ''
  });

  // Breach reason modal state
  const [breachReasonModal, setBreachReasonModal] = useState<{
    isOpen: boolean;
    targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION';
  }>({
    isOpen: false,
    targetType: undefined
  });

  // Access lost modal state
  const [accessLostModalOpen, setAccessLostModalOpen] = useState(false);

  // Assignment group change confirmation modal state
  const [groupChangeConfirmModal, setGroupChangeConfirmModal] = useState<{
    isOpen: boolean;
    newGroupId: string | null;
    newGroupName: string;
    pendingChanges: IncidentUpdatePayload;
  }>({
    isOpen: false,
    newGroupId: null,
    newGroupName: '',
    pendingChanges: {}
  });

  const serviceNowLinkRefetchInterval = useServiceNowLinkRefetchInterval(id, 'INCIDENT');

  const { data: incident, isLoading, error } = useQuery({
    queryKey: ['incident', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data;
    },
    refetchInterval: (query) => serviceNowLinkRefetchInterval(query),
  });

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery<WorkNote[]>({
    queryKey: ['case', id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/work-notes`);
      return response.data;
    },
    enabled: !!id && activeTab === 'notes',
  });

  const { data: serviceNowHoldReasonData } = useQuery<HoldReasonOption[]>({
    queryKey: ['servicenow-incident-hold-reasons', incident?.customerId],
    queryFn: async () => {
      const response = await api.get(
        `/integrations/servicenow/customers/${incident!.customerId}/incident-hold-reasons`
      );
      return response.data.data ?? [];
    },
    enabled: Boolean(incident?.customerId) && onHoldModal.isOpen,
    staleTime: 5 * 60 * 1000,
  });
  // Avoid `= []` default — a new array each render would reset the on-hold modal text.
  const serviceNowHoldReasonOptions = serviceNowHoldReasonData ?? EMPTY_HOLD_REASON_OPTIONS;

  const hasLoggedView = useRef(false);

  // Reset local UI state when navigating to a different incident (prevents stale edit form / tabs).
  useEffect(() => {
    hasLoggedView.current = false;
    setIsEditing(false);
    setEditForm({});
    setNewNote('');
    setNoteType('work_note');
    setErrors({});
    setActiveTab('details');
    setPreviousEngagementId(null);
    setPreviousCustomerId(null);
    setImpactQuery('');
    setUrgencyQuery('');
    setChannelQuery('');
    setCategoryQuery('');
    setServicePlanQuery('');
    setAssignmentGroupQuery('');
    setContactQuery('');
    setDuplicateModalOpen(false);
    setCreateProblemModalOpen(false);
    setRecordTimeModalOpen(false);
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      targetState: '',
      confirmLabel: 'Confirm',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    });
    if (id) {
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['sla-instances', 'INCIDENT', id] });
    }
  }, [id, queryClient]);

  // Log Incident Details on mount when incident data is available
  useEffect(() => {
    if (incident && user && !hasLoggedView.current) {
      hasLoggedView.current = true;
      trackIncidentOperation(
        'Incident Details',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Viewed Incident Details',
          incidentNumber: incident.number || incident.id || id || 'Unknown',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [incident, user, isServiceDeskUser, id]);

  // Fetch SLA instances for this incident
  const { data: slaInstances } = useQuery({
    queryKey: ['sla-instances', 'INCIDENT', id],
    queryFn: async () => {
      const response = await api.get(`/sla-instances/INCIDENT/${id}`);
      return response.data;
    },
    enabled: !!id,
    // Stop refetching when all SLAs are in terminal states (BREACHED, ACHIEVED, COMPLETED, CANCELLED)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (areAllSlaInstancesTerminal(data)) {
        return false; // Stop polling
      }
      return 30000; // Continue polling every 30 seconds
    }
  });

  // Subscribe to real-time SLA events via SSE
  // This will trigger immediate query invalidation when SLA events occur (STARTED, PAUSED, etc.)
  useSlaEvents('INCIDENT', id);

  // Get ticket permissions based on backend access control and user role
  const { canEdit, canEditAssignment, isAssignmentOnlyEditMode, canChangeState, canAddWorkNote, canAddAttachments, canAddRelated, canPerformActions, editAccessReason } = useTicketPermissions(
    incident,
    'incidents'
  );

  const { data: usersData } = useQuery({
    queryKey: ['users', 'dropdown'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
    enabled: isEditing,
  });

  useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
    enabled: isEditing,
  });

  useQuery({
    queryKey: ['engagements', editForm.customerId],
    queryFn: async () => {
      if (!editForm.customerId) return { engagements: [] };
      const response = await api.get(`/engagements?customerId=${editForm.customerId}`);
      return response.data;
    },
    enabled: isEditing && !!editForm.customerId
  });

  const { data: servicePlansData } = useQuery({
    queryKey: ['servicePlans', editForm.engagementId],
    queryFn: async () => {
      if (!editForm.engagementId) return { servicePlans: [] };
      const response = await api.get(`/engagements/${editForm.engagementId}/service-plans`);
      return response.data;
    },
    enabled: isEditing && !!editForm.engagementId
  });


  // Fetch contacts for the selected customer - only when editing
  const { data: contactsData } = useQuery({
    queryKey: ['engagement-contacts', editForm.customerId || incident?.customerId],
    queryFn: async () => {
      const customerId = editForm.customerId || incident?.customerId || incident?.Customer?.id;
      if (!customerId) return { users: [], total: 0 };
      const response = await api.get(`/users?customerId=${customerId}&class=contact&limit=1000`);
      return response.data;
    },
    enabled: isEditing && !!(editForm.customerId || incident?.customerId || incident?.Customer?.id)
  });

  // Fetch assignment groups
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      try {
        const response = await api.get('/groups/assignment-groups');
        return response.data || [];
      } catch (error) {
        console.error('Error fetching assignment groups:', error);
        return [];
      }
    },
    enabled: isEditing,
  });

  // Fetch group members when assignment group is selected
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', editForm.assignmentGroupId, editForm.customerId || incident?.customerId || incident?.Customer?.id],
    queryFn: async () => {
      if (!editForm.assignmentGroupId) return [];
      try {
        return await fetchAssignmentGroupMembers(
          editForm.assignmentGroupId,
          editForm.customerId || incident?.customerId || incident?.Customer?.id || null
        );
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!editForm.assignmentGroupId
  });

  // Clear engagement and service plan when customer changes
  useEffect(() => {
    if (previousCustomerId !== null && previousCustomerId !== editForm.customerId) {
      setEditForm(prev => ({ ...prev, engagementId: null, servicePlanId: null }));
    }
    setPreviousCustomerId(editForm.customerId || null);
  }, [editForm.customerId, previousCustomerId]);

  // Clear service plan when engagement changes
  useEffect(() => {
    if (previousEngagementId !== null && previousEngagementId !== editForm.engagementId) {
      setEditForm(prev => ({ ...prev, servicePlanId: null }));
    }
    setPreviousEngagementId(editForm.engagementId || null);
  }, [editForm.engagementId, previousEngagementId]);

  // Clear assigned to when assignment group changes
  useEffect(() => {
    // Only clear if we're actively changing the group (not on initial load)
    if (editForm.assignmentGroupId !== incident?.assignmentGroupId) {
      setEditForm(prev => ({ ...prev, assignedTo: null }));
    }
  }, [editForm.assignmentGroupId]);

  // Track previous state for state change logging
  const previousStateRef = useRef<string | undefined>(undefined);

  // Update previous state when incident data changes
  useEffect(() => {
    if (incident?.state) {
      previousStateRef.current = incident.state;
    }
  }, [incident?.state]);

  const updateIncidentMutation = useMutation({
    mutationFn: async (data: IncidentUpdatePayload) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: (updated: any, variables: IncidentUpdatePayload) => {
      // Enrich caller/assignee if backend omitted full objects (demo mode)
      const users = usersData?.users || [];
      const withUsers = { ...updated } as any;
      if (!withUsers.contact && editForm.contactId) {
        const u = users.find((x: any) => x.id === editForm.contactId);
        if (u) withUsers.contact = { id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email, name: u.name };
      }
      if (!withUsers.assignedTo && editForm.assignedTo) {
        const u = users.find((x: any) => x.id === editForm.assignedTo);
        if (u) withUsers.assignedTo = { id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email, name: u.name };
      }

      // Update cache with new data to prevent error when access is lost
      queryClient.setQueryData(['incident', id], withUsers);

      // Check if this was a confirmed assignment group change - if so, redirect to list
      if (groupChangeConfirmModal.isOpen) {
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingChanges: {}
        });

        // Invalidate list queries to ensure fresh data on redirect
        queryClient.invalidateQueries({ queryKey: ['incidents'] });
        queryClient.invalidateQueries({ queryKey: ['cases'] });

        toast.success('Incident updated successfully. Redirecting to list...');
        setTimeout(() => navigateBackToIncidents(), 500);
        return;
      }

      // Check if user lost access after the update
      if (updated.canEdit === false && !isServiceDeskUser && !updated.canEditAssignment) {
        setAccessLostModalOpen(true);
        return;
      }

      // Invalidate queries only if user still has access
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setIsEditing(false);
      setErrors({});

      // Show success message for assignment if it was an assignment operation
      if (updated.assignedToId || updated.assignedTo) {
        toast.success('Incident assigned successfully');
      }

      // Log state change if state was changed
      if (variables.state && previousStateRef.current && variables.state !== previousStateRef.current && user) {
        const stateLabels: { [key: string]: string } = {
          'new': 'New',
          'in_progress': 'In Progress',
          'on_hold': 'On Hold',
          'resolved': 'Resolved',
          'closed': 'Closed',
          'cancelled': 'Cancelled',
        };
        const oldStatus = stateLabels[previousStateRef.current] || previousStateRef.current;
        const newStatus = stateLabels[variables.state] || variables.state;
        
        trackIncidentOperation(
          'Incident Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Incident State Changed',
            incidentNumber: updated.number || incident?.number || id || 'Unknown',
            oldStatus,
            newStatus,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
        previousStateRef.current = variables.state;
      } else if (!variables.state && Object.keys(variables).length > 0 && user) {
        // Log details modification (save without state change)
        trackIncidentOperation(
          'Incident Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Incident Details Modified',
            incidentNumber: updated.number || incident?.number || id || 'Unknown',
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update case';
      setErrors({ submit: message });
      toast.error(message);
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: async (data: { note: string; isPublic: boolean }) => {
      const response = await api.post(`/incidents/${id}/work-notes`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setNewNote('');
      
      // Log note addition with note type in action
      if (user && incident) {
        const noteTypeLabel = variables.isPublic ? 'Customer Note' : 'Work Note';
        trackIncidentOperation(
          'Incident Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: `Note Added - ${noteTypeLabel}`,
            incidentNumber: incident.number || id || 'Unknown',
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    }
  });

  // Breach reason mutation
  const breachReasonMutation = useMutation({
    mutationFn: async ({ breachReason, targetType }: { breachReason: string; targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' }) => {
      const response = await api.put(`/cases/${id}/breach-reason`, { breachReason, targetType });
      return response.data;
    },
    onSuccess: async () => {
      // Use refetchQueries to ensure data is fresh before closing modal
      await queryClient.refetchQueries({ queryKey: ['incident', id] });
      // Refetch list queries so breach reason shows without browser refresh
      queryClient.refetchQueries({ queryKey: ['incidents'] });
      queryClient.refetchQueries({ queryKey: ['cases'] });
      toast.success('Breach reason saved successfully');
      setBreachReasonModal({ isOpen: false, targetType: undefined });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to save breach reason';
      toast.error(message);
    }
  });

  // Computed values for SLA breach status
  const hasSlaBreached = React.useMemo(() => {
    // Check incident's direct slaBreached field first
    if (incident?.slaBreached) return true;
    // Also check SLA instances
    if (!slaInstances) return false;
    return slaInstances.some((instance: any) =>
      instance.currentState === 'BREACHED' || instance.isBreached
    );
  }, [incident?.slaBreached, slaInstances]);

  // Check if breach reasons are provided for all breached SLAs
  const breachReasons = {
    response: incident?.responseBreachReason,
    ongoing: incident?.ongoingBreachReason,
    resolution: incident?.resolutionBreachReason
  };

  // Helper to check if a specific SLA target type is breached
  const isSlaTargetBreached = (targetType: string): boolean => {
    if (!slaInstances) return false;
    return slaInstances.some(
      (instance: any) =>
        // Check both instance.targetType and instance.SlaPolicy.targetType for compatibility
        (instance.targetType === targetType || instance.SlaPolicy?.targetType === targetType) &&
        (instance.currentState === 'BREACHED' || instance.isBreached)
    );
  };

  // Check if all breached SLAs have breach reasons
  const hasAllBreachReasons = (): boolean => {
    if (!hasSlaBreached) return true;

    // Check each breached SLA target type for its breach reason
    const responseBreached = isSlaTargetBreached('RESPONSE');
    const ongoingBreached = isSlaTargetBreached('ONGOING');
    const resolutionBreached = isSlaTargetBreached('RESOLUTION');

    if (responseBreached && !incident?.responseBreachReason) return false;
    if (ongoingBreached && !incident?.ongoingBreachReason) return false;
    if (resolutionBreached && !incident?.resolutionBreachReason) return false;

    return true;
  };

  // Legacy check for backward compatibility
  const hasBreachReason = hasAllBreachReasons();

  // Render AccessLostModal at top level so it always shows when open
  if (accessLostModalOpen) {
    return (
      <>
        <AccessLostModal
          isOpen={accessLostModalOpen}
          onConfirm={() => {
            setAccessLostModalOpen(false);
            navigateBackToIncidents();
          }}
          ticketNumber={incident?.number}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !incident) {
    return (
      <TicketAccessDenied
        ticketType="Incident"
        ticketNumber={incident?.number}
        listPageUrl={
          incidentsListSearch !== undefined ? `/incidents${incidentsListSearch}` : '/incidents'
        }
      />
    );
  }

  const handleEdit = () => {
    setEditForm({
      shortDescription: incident.shortDescription,
      description: incident.description,
      priority: incident.priority,
      impact: incident.impact || 'low_3',
      urgency: incident.urgency || 'low_3',
      state: incident.state,
      category: incident.category,
      resolutionNotes: getIncidentResolutionNotes(incident),
      resolutionCode: incident.resolutionCode,
      contactId: incident.contactId || incident.contact?.id || null,
      assignedTo: incident.assignedTo?.id || incident.assignedTo?._id || null,
      assignmentGroupId: incident.assignmentGroupId || incident.AssignmentGroup?.id || null,
      parentId: incident.parentId || null,
      customerId: incident.customerId || incident.Customer?.id || null,
      engagementId: incident.engagementId || incident.Engagement?.id || null,
      servicePlanId: incident.servicePlanId || incident.ServicePlan?.id || null,
      resourceId: incident.resourceId || incident.Resource?.id || null,
      channel: incident.channel || 'web'
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (isAssignmentOnlyEditMode) {
      if (!editForm.assignmentGroupId) {
        setErrors({ assignmentGroupId: 'Assignment Group is required' });
        return;
      }

      setErrors({});
      updateIncidentMutation.mutate(buildAssignmentOnlyPayload(editForm));
      return;
    }

    // Validate required fields
    const newErrors: Record<string, string> = {};

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Only send fields that have actually changed
    const changedFields: any = {};
    const originalValues = {
      shortDescription: incident.shortDescription,
      description: incident.description,
      priority: incident.priority,
      impact: incident.impact || 'low_3',
      urgency: incident.urgency || 'low_3',
      state: incident.state,
      category: incident.category,
      resolutionNotes: getIncidentResolutionNotes(incident),
      resolutionCode: incident.resolutionCode,
      contactId: incident.contactId || incident.contact?.id || null,
      assignedTo: incident.assignedTo?.id || incident.assignedTo?._id || null,
      assignmentGroupId: incident.assignmentGroupId || incident.AssignmentGroup?.id || null,
      parentId: incident.parentId || null,
      customerId: incident.customerId || incident.Customer?.id || null,
      engagementId: incident.engagementId || incident.Engagement?.id || null,
      servicePlanId: incident.servicePlanId || incident.ServicePlan?.id || null,
      resourceId: incident.resourceId || incident.Resource?.id || null,
      channel: incident.channel || 'web'
    };

    // Compare each field and only include if changed
    (Object.keys(editForm) as Array<keyof typeof editForm>).forEach(key => {
      if (key in originalValues && editForm[key] !== (originalValues as any)[key]) {
        changedFields[key] = editForm[key];
      } else if (!(key in originalValues) && editForm[key] !== undefined) {
        // Include new fields that weren't in originalValues
        changedFields[key] = editForm[key];
      }
    });

    // Check if assignment group is changing and user is not a member of the new group
    // Only check for regular users (user/agent roles), not for super_admin/admin/manager
    // Service Desk users can access all tickets, so skip the warning for them
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    if (changedFields.assignmentGroupId && !isGlobalAccessRole && !isServiceDeskUser) {
      const newGroupId = changedFields.assignmentGroupId;
      // Check if user is a member of the new group by checking group members
      // groupMembersData is already fetched for the selected assignment group
      const newGroupMembers = groupMembersData || [];
      const isUserMemberOfNewGroup = newGroupMembers.some((member: any) =>
        member.id === user?.id || member._id === user?.id
      );

      if (!isUserMemberOfNewGroup) {
        // Find the new group name
        const newGroup = (assignmentGroupsData || []).find((group: any) =>
          group.id === newGroupId || group._id === newGroupId
        );
        const newGroupName = newGroup?.name || 'Unknown Group';

        // Show confirmation modal instead of saving immediately
        setGroupChangeConfirmModal({
          isOpen: true,
          newGroupId,
          newGroupName,
          pendingChanges: changedFields
        });
        return;
      }
    }

    updateIncidentMutation.mutate(changedFields);
  };

  // Handler for confirming assignment group change
  const handleConfirmGroupChange = () => {
    // Save the changes and redirect to list after successful save
    updateIncidentMutation.mutate(groupChangeConfirmModal.pendingChanges);
    // Note: Navigation to list will happen in mutation's onSuccess
  };

  // Handler for cancelling assignment group change
  const handleCancelGroupChange = () => {
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingChanges: {}
    });
  };

  const handleAssignToMe = () => {
    if (user?.id) {
      updateIncidentMutation.mutate({ assignedToId: user.id });
    }
  };

  // Check if "Assign To Me" button should be shown
  const shouldShowAssignToMeButton = () => {
    if (!user?.id || !incident || isEditing) return false;

    // Don't show if already assigned to current user
    const currentAssignedToId = incident.assignedToId || incident.assignedTo?.id || incident.assignedTo?._id;
    if (currentAssignedToId === user.id) return false;

    // Backend computes membership during GET /cases/:id (avoids fetching all group members)
    return Boolean(incident.canAssignToSelf);
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    addNoteMutation.mutate({
      note: sanitizeNoteInput(newNote),
      isPublic: noteType === 'customer_note'
    });
  };

  const handleStateChange = (newState: string) => {
    const stateLabel = getStateLabel(newState);
    const currentState = incident?.state;

    // Prevent state changes if Assigned To is missing (except for new state)
    if (!incident.assignedTo && !incident.assignedToId && newState !== 'new') {
      setErrors({
        submit: 'Cannot change incident state without assigning it to a team member. Please assign the incident first.'
      });
      return;
    }

    // Show on-hold modal if transitioning to on_hold state
    if (newState === 'on_hold') {
      setOnHoldModal({
        isOpen: true,
        title: 'Put Incident On Hold',
        message: 'Please provide a reason for putting this incident on hold:'
      });
      return;
    }

    // Check for breach reason before allowing resolved/closed transitions
    console.log('[DEBUG] Breach check:', { newState, hasSlaBreached, hasBreachReason, slaBreached: incident?.slaBreached });
    if ((newState === 'resolved' || newState === 'closed') && hasSlaBreached && !hasBreachReason) {
      // Find the first breached SLA without a reason
      let missingTargetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' | undefined;
      if (isSlaTargetBreached('RESPONSE') && !incident?.responseBreachReason) {
        missingTargetType = 'RESPONSE';
      } else if (isSlaTargetBreached('ONGOING') && !incident?.ongoingBreachReason) {
        missingTargetType = 'ONGOING';
      } else if (isSlaTargetBreached('RESOLUTION') && !incident?.resolutionBreachReason) {
        missingTargetType = 'RESOLUTION';
      }

      if (missingTargetType) {
        const targetLabel = missingTargetType.charAt(0) + missingTargetType.slice(1).toLowerCase();
        toast.error(`Please provide a breach reason for the ${targetLabel} SLA before resolving or closing this ticket.`);
        setBreachReasonModal({ isOpen: true, targetType: missingTargetType });
        return;
      }
    }

    // Show resolution modal if transitioning to resolved state
    if (newState === 'resolved') {
      setResolutionModal({
        isOpen: true,
        title: 'Resolve Incident',
        message: 'Please provide detailed resolution notes explaining how this incident was resolved:'
      });
      return;
    }

    // Show cancellation modal if transitioning to cancelled state
    if (newState === 'cancelled') {
      setCancellationModal({
        isOpen: true,
        title: 'Cancel Incident',
        message: 'Please provide a reason for cancelling this incident:'
      });
      return;
    }

    // Show closure modal if transitioning to closed state
    if (newState === 'closed') {
      setClosureModal({
        isOpen: true,
        title: 'Close Incident',
        message: 'Please provide closure notes explaining why this incident is being closed:'
      });
      return;
    }

    // Prevent reopening from closed state
    if (currentState === 'closed') {
      // Closed incidents cannot be reopened
      return;
    }

    // Detect reopening: transitioning from cancelled or resolved to an earlier state
    const closedStates = ['cancelled', 'resolved'];
    const activeStates = ['new', 'assigned', 'in_progress'];
    if (closedStates.includes(currentState) && activeStates.includes(newState)) {
      setReopenModal({
        isOpen: true,
        title: 'Reopen Incident',
        message: 'Please provide a reason for reopening this incident:'
      });
      return;
    }

    // Configure modal based on state
    let confirmButtonClass = 'bg-blue-600 hover:bg-blue-700 text-white';
    if (newState === 'closed') {
      confirmButtonClass = 'bg-gray-600 hover:bg-gray-700 text-white';
    }

    setConfirmModal({
      isOpen: true,
      title: 'Confirm State Change',
      message: `Are you sure you want to change the incident state to "${stateLabel}"?`,
      targetState: newState,
      confirmLabel: `Change to ${stateLabel}`,
      confirmButtonClass
    });
  };

  const handleConfirmStateChange = () => {
    updateIncidentMutation.mutate({ state: confirmModal.targetState });
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmOnHold = (reason: string) => {
    const onHoldReason = serviceNowHoldReasonOptions.length
      ? serviceNowHoldReasonOptions.find((option) => option.value === reason)?.label ?? reason
      : reason;
    updateIncidentMutation.mutate({ state: 'on_hold', onHoldReason });
    setOnHoldModal({ isOpen: false, title: '', message: '' });
  };

  const handleCloseOnHoldModal = () => {
    setOnHoldModal({ isOpen: false, title: '', message: '' });
  };

  const handleConfirmResolution = (data: { resolutionCode: string; resolutionNotes: string }) => {
    // Use state-transition endpoint for proper resolution handling
    api.put(`/cases/${id}/state-transition`, {
      newState: 'resolved',
      resolutionCode: data.resolutionCode,
      resolutionNotes: data.resolutionNotes
    }).then((response) => {
      const updatedCase = response?.data || response;
      // Merge into cache immediately so Closure tab shows resolutionNotes without waiting on refetch
      queryClient.setQueryData(['incident', id], (prev: any) => ({
        ...(prev || {}),
        ...(updatedCase || {}),
        state: 'resolved',
        resolutionCode: data.resolutionCode,
        resolutionNotes: data.resolutionNotes,
        resolution: data.resolutionNotes,
        resolvedBy: updatedCase?.User_Case_resolvedByIdToUser || updatedCase?.resolvedBy || prev?.resolvedBy,
        resolvedAt: updatedCase?.resolvedAt || prev?.resolvedAt || new Date().toISOString(),
      }));
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setResolutionModal({ isOpen: false, title: '', message: '' });
      toast.success('Incident resolved successfully');

      // Log state transition to resolved
      if (user && incident) {
        const stateLabels: { [key: string]: string } = {
          'new': 'New',
          'in_progress': 'In Progress',
          'on_hold': 'On Hold',
          'resolved': 'Resolved',
          'closed': 'Closed',
          'cancelled': 'Cancelled',
        };
        const oldState = previousStateRef.current || incident.state || 'Unknown';
        const oldStatus = stateLabels[oldState] || oldState;
        const newStatus = 'Resolved';
        const incidentNumber = updatedCase?.number || incident?.number || id || 'Unknown';
        
        trackIncidentOperation(
          'Incident Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Incident State Changed',
            incidentNumber: incidentNumber,
            oldStatus,
            newStatus,
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
        previousStateRef.current = 'resolved';
      }
    }).catch((error) => {
      console.error('Failed to resolve incident:', error);
      toast.error(error.response?.data?.message || 'Failed to resolve incident');
      setResolutionModal({ isOpen: false, title: '', message: '' });
    });
  };

  const handleCloseResolutionModal = () => {
    setResolutionModal({ isOpen: false, title: '', message: '' });
  };

  const handleConfirmCancellation = (reason: string) => {
    updateIncidentMutation.mutate({ state: 'cancelled', cancellationReason: reason });
    setCancellationModal({ isOpen: false, title: '', message: '' });
  };

  const handleCloseCancellationModal = () => {
    setCancellationModal({ isOpen: false, title: '', message: '' });
  };

  const handleConfirmReopen = (reason: string) => {
    // Reopen to new state
    updateIncidentMutation.mutate({ state: 'new', reopenReason: reason });
    setReopenModal({ isOpen: false, title: '', message: '' });
  };

  const handleCloseReopenModal = () => {
    setReopenModal({ isOpen: false, title: '', message: '' });
  };

  const handleConfirmClosure = (closureNotes: string) => {
    // Store priority before closure (in case it changes)
    const currentPriority = incident?.priority;
    
    // Use state-transition endpoint for proper closure handling
    api.put(`/cases/${id}/state-transition`, {
      newState: 'closed',
      closureNotes: closureNotes
    }).then((response) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      
      // Check priority and trigger survey email logic
      // Use stored priority since incident data might not be refreshed yet
      const priority = currentPriority || incident?.priority;
      
      // Critical or High priority: Automatically send survey
      if (priority === 'critical_1' || priority === 'high_2') {
        sendSurveyEmail();
      } 
      // Moderate or Low priority: Show confirmation popup
      else if (priority === 'moderate_3' || priority === 'low_4') {
        setSurveyConfirmModal({ isOpen: true });
      }
      
      // Log state transition to closed
      if (user && incident) {
        const stateLabels: { [key: string]: string } = {
          'new': 'New',
          'in_progress': 'In Progress',
          'on_hold': 'On Hold',
          'resolved': 'Resolved',
          'closed': 'Closed',
          'cancelled': 'Cancelled',
        };
        const oldState = previousStateRef.current || incident.state || 'Unknown';
        const oldStatus = stateLabels[oldState] || oldState;
        const newStatus = 'Closed';
        const updatedCase = response?.data || response;
        const incidentNumber = updatedCase?.number || incident?.number || id || 'Unknown';
        
        trackIncidentOperation(
          'Incident Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Incident State Changed',
            incidentNumber: incidentNumber,
            oldStatus,
            newStatus,
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
        previousStateRef.current = 'closed';
      }
    }).catch((error) => {
      console.error('Failed to close incident:', error);
      toast.error(error.response?.data?.message || 'Failed to close incident');
    });
    setClosureModal({ isOpen: false, title: '', message: '' });
  };

  const sendSurveyEmail = async () => {
    try {
      const response = await api.post(`/cases/${id}/send-survey`);
      if (response.data.success) {
        setSurveyResultModal({
          isOpen: true,
          type: 'success',
          message: 'Survey has been sent to the customer.'
        });
      } else {
        setSurveyResultModal({
          isOpen: true,
          type: 'error',
          message: 'Failed to send survey email'
        });
      }
    } catch (error: any) {
      console.error('Failed to send survey email:', error);
      setSurveyResultModal({
        isOpen: true,
        type: 'error',
        message: error.response?.data?.message || 'Failed to send survey email'
      });
    }
  };

  const handleSurveyConfirmYes = () => {
    setSurveyConfirmModal({ isOpen: false });
    sendSurveyEmail();
  };

  const handleSurveyConfirmNo = () => {
    setSurveyConfirmModal({ isOpen: false });
  };

  const handleCloseClosureModal = () => {
    setClosureModal({ isOpen: false, title: '', message: '' });
  };


  const getStateLabel = (state: string): string => {
    const found = workflowSteps.find(s => s.id === state);
    if (found) return found.name;
    const map: Record<string, string> = {
      open: 'Open',
      on_hold: 'On Hold',
      cancelled: 'Cancelled'
    };
    return map[state] || state;
  };

  const getCurrentStepIndex = () => {
    // Map cancelled state to the last step (closed position)
    if (incident.state === 'cancelled') {
      return workflowSteps.length - 1; // Returns 3 (closed step index)
    }
    return workflowSteps.findIndex(step => step.id === incident.state);
  };


  const formatUserName = (
    user?: { firstName?: string; lastName?: string; name?: string; email?: string } | null
  ): string => {
    if (!user) return 'Unassigned';
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    const full = [first, last].filter(Boolean).join(' ');
    if (full) return full;
    if (user.name) return user.name;
    if (user.email) return user.email;
    return 'Unassigned';
  };


  // Compute priority UI values when incident is available
  const currentPriority = incident?.priority || 'NORMAL';
  const priorityClass = priorityColors[currentPriority] || 'bg-gray-100 text-gray-800 border-gray-200';
  const priorityText = priorityLabels[currentPriority] || currentPriority;

  // Check if incident is unassigned
  const isUnassigned = !incident?.assignedTo && !incident?.assignedToId;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={navigateBackToIncidents}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Incidents
          </button>

          <div className="flex gap-2">
            {/* Assign To Me Button */}
            {shouldShowAssignToMeButton() && (
              <button
                onClick={handleAssignToMe}
                disabled={updateIncidentMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {updateIncidentMutation.isPending ? 'Assigning...' : 'Assign To Me'}
              </button>
            )}

            {/* Record Time Button - Show when assigned to current user and state is not new/closed/cancelled */}
            {incident &&
             user &&
             (incident.assignedTo?.id === user.id || incident.assignedToId === user.id) &&
             incident.state !== 'new' &&
             incident.state !== 'closed' &&
             incident.state !== 'cancelled' &&
             incident.Customer &&
             incident.Engagement &&
             incident.AssignmentGroup && (
              <button
                onClick={() => setRecordTimeModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ClockIcon className="h-5 w-5 mr-2" />
                Record Time
              </button>
            )}

            {/* Actions Dropdown - Show if user can perform actions (not state-restricted) */}
            {canPerformActions && (
              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <TicketIcon className="h-5 w-5 mr-2" />
                    Actions
                    <ChevronDownIcon className="ml-2 h-4 w-4" />
                  </Menu.Button>
                </div>

                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setDuplicateModalOpen(true)}
                          className={`${
                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                          } flex items-center w-full px-4 py-2 text-sm`}
                        >
                          <TicketIcon className="h-5 w-5 mr-3" />
                          Duplicate Incident
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setCreateProblemModalOpen(true)}
                          className={`${
                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                          } flex items-center w-full px-4 py-2 text-sm`}
                        >
                          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                          Create Problem
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Menu>
            )}
          </div>
        </div>

        {/* Incident Workflow Progress */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-6">
          <div className="px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Incident Workflow Progress</h3>
            <p className="text-sm text-gray-500 mt-1">Track the journey of this incident through its lifecycle</p>
          </div>
          <div className="px-6 pb-6">
            <nav aria-label="Progress">
              <ol className="flex items-center">
                {workflowSteps.map((step, stepIdx) => {
                  const currentIdx = getCurrentStepIndex();
                  const Icon = step.icon;
                  const isComplete = stepIdx < currentIdx;
                  const isCurrent = stepIdx === currentIdx;
                  const isCancelled = incident.state === 'cancelled';
                  const isLastStep = stepIdx === workflowSteps.length - 1;
                  const showCancelledIcon = isLastStep && isCurrent && isCancelled;

                  return (
                    <li key={step.id} className={stepIdx !== workflowSteps.length - 1 ? 'flex-1' : ''}>
                      <div className="flex items-center">
                        <div className={`relative flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                          showCancelledIcon
                            ? 'bg-red-100 border-transparent text-red-600'
                            : isComplete
                              ? (isCancelled ? 'bg-white border-gray-300' : 'bg-indigo-600 border-transparent')
                              : isCurrent
                                ? 'bg-indigo-600 border-transparent'
                                : 'bg-white border-gray-300'
                        }`}>
                          <Icon className={`h-6 w-6 ${
                            showCancelledIcon
                              ? 'text-red-600'
                              : isComplete
                                ? (isCancelled ? 'text-gray-400' : 'text-white')
                                : isCurrent
                                  ? 'text-white'
                                  : 'text-gray-400'
                          }`} />
                        </div>
                        {stepIdx !== workflowSteps.length - 1 && (
                          <div className={`ml-4 flex-1 h-0.5 ${
                            isComplete ? (isCancelled ? 'bg-red-300' : 'bg-indigo-600') : 'bg-gray-300'
                          }`} />
                        )}
                      </div>
                      <div className="mt-2">
                        <span className={`text-sm font-medium ${
                          isComplete || isCurrent ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {showCancelledIcon ? 'Cancelled' : step.name}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </nav>

            {/* Status Bar with Action Buttons */}
            <div className="mt-6 px-6 py-3 bg-gray-50 border-t">
              <div className="flex items-center justify-between">
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Current Status:</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {incident.state.replace('_', ' ')}
                    </span>
                  </div>
                  {incident.state === 'on_hold' && incident.onHoldReason && (
                    <div className="flex items-start space-x-2 mt-2">
                      <span className="text-sm text-gray-600">On-Hold Reason:</span>
                      <div className="text-sm text-gray-800 italic">
                        <RichTextDisplay content={incident.onHoldReason} />
                      </div>
                    </div>
                  )}
                </div>
                {incident.state !== 'closed' && canChangeState && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Change to:</span>
                    {incident.state === 'new' && (
                    <button
                      onClick={() => handleStateChange('in_progress')}
                      disabled={updateIncidentMutation.isPending || isUnassigned}
                      className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      title={isUnassigned ? 'Please assign incident before changing state' : ''}
                    >
                      In Progress
                    </button>
                  )}

                  {incident.state === 'open' && (
                    <>
                      <button
                        onClick={() => handleStateChange('on_hold')}
                        disabled={updateIncidentMutation.isPending || isUnassigned}
                        className="px-4 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={isUnassigned ? 'Please assign incident before changing state' : ''}
                      >
                        On Hold
                      </button>
                      <button
                        onClick={() => handleStateChange('in_progress')}
                        disabled={updateIncidentMutation.isPending || isUnassigned}
                        className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={isUnassigned ? 'Please assign incident before changing state' : ''}
                      >
                        In Progress
                      </button>
                    </>
                  )}

                  {incident.state === 'in_progress' && (
                    <>
                      <button
                        onClick={() => handleStateChange('on_hold')}
                        disabled={updateIncidentMutation.isPending || isUnassigned}
                        className="px-4 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={isUnassigned ? 'Please assign incident before changing state' : ''}
                      >
                        On Hold
                      </button>
                      <button
                        onClick={() => handleStateChange('resolved')}
                        disabled={updateIncidentMutation.isPending || isUnassigned}
                        className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={isUnassigned ? 'Please assign incident before changing state' : ''}
                      >
                        Resolved
                      </button>
                    </>
                  )}

                  {incident.state === 'on_hold' && (
                    <button
                      onClick={() => handleStateChange('in_progress')}
                      disabled={updateIncidentMutation.isPending || isUnassigned}
                      className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      title={isUnassigned ? 'Please assign incident before changing state' : ''}
                    >
                      Resume (In Progress)
                    </button>
                  )}

                  {incident.state === 'resolved' && (
                    <>
                      <button
                        onClick={() => handleStateChange('closed')}
                        disabled={updateIncidentMutation.isPending || isUnassigned}
                        className="px-4 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={isUnassigned ? 'Please assign incident before changing state' : ''}
                      >
                        Closed
                      </button>
                      <button
                        onClick={() => handleStateChange('in_progress')}
                        disabled={updateIncidentMutation.isPending || isUnassigned}
                        className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={isUnassigned ? 'Please assign incident before changing state' : ''}
                      >
                        Reopen
                      </button>
                    </>
                  )}

                  {(incident.state !== 'cancelled' && incident.state !== 'closed' && incident.state !== 'resolved') && (
                    <button
                      onClick={() => handleStateChange('cancelled')}
                      disabled={updateIncidentMutation.isPending || isUnassigned}
                      className="px-4 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      title={isUnassigned ? 'Please assign incident before changing state' : ''}
                    >
                      Cancelled
                    </button>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Details Header */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-4 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">⚠️</span>
                <h1 className="text-xl font-semibold text-gray-900">
                  {incident.number}{incident.shortDescription && <span className="text-base font-normal text-gray-600"> - {stripHtml(incident.shortDescription)}</span>}
                </h1>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Created on {formatDateTime(incident.createdAt)}
                {incident.createdFromServiceNow && (
                  <span className="ml-2 text-indigo-700">
                    Linked from {getExternalSystemDisplayName(incident.serviceNowLink?.externalSystem)}
                  </span>
                )}
                {incident.syncedToServiceNow && (
                  <span className="ml-2 text-indigo-700">
                    Linked to {getExternalSystemDisplayName(incident.serviceNowLink?.externalSystem)}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 flex-wrap flex-shrink-0">
              <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                {getStateLabel(incident.state)}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${priorityClass}`}>
                Priority {priorityText}
              </span>
              <ServiceNowKnownField
                placement="header"
                pulseNumber={incident.number}
                serviceNowNumber={incident.serviceNowNumber}
                serviceNowKnown={incident.serviceNowKnown}
                serviceNowLinked={incident.serviceNowLinked}
                createdFromServiceNow={incident.createdFromServiceNow}
                syncedToServiceNow={incident.syncedToServiceNow}
                externalSystem={incident.serviceNowLink?.externalSystem}
              />
            </div>
          </div>
        </div>

        {/* Warning Banner - Show when Assigned To is missing */}
        {!incident.assignedTo && !incident.assignedToId && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <span className="font-medium">Action Required:</span> This incident needs to be assigned before work can begin. Please assign this incident to a team member to proceed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Resolution Notes - Show when incident is resolved */}
        {(incident.state === 'resolved' || incident.state === 'closed') && getIncidentResolutionNotes(incident) && (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-4 px-6 py-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Resolution Notes</h3>
                <div className="text-sm text-gray-700 bg-green-50 border border-green-200 rounded-md p-4">
                  <RichTextDisplay content={getIncidentResolutionNotes(incident)} />
                </div>
                {incident.resolutionCode && (
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-medium">Resolution Code:</span> {incident.resolutionCode}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SLA Timers */}
        <SlaInstancesDisplay
          slaInstances={slaInstances}
          onBreachDetected={() => {
            // Trigger immediate refetch when UI detects breach
            queryClient.invalidateQueries({ queryKey: ['sla-instances', 'INCIDENT', id] });
          }}
          breachReasons={breachReasons}
          onRequestBreachReason={(targetType) => setBreachReasonModal({ isOpen: true, targetType })}
        />

        {/* Tab Navigation */}
        <div className="bg-white shadow-sm rounded-t-lg border border-gray-200">
          <nav className="flex border-b border-gray-200" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`${
                activeTab === 'details'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                  : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
              } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`${
                activeTab === 'notes'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                  : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
              } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('attachments')}
              className={`${
                activeTab === 'attachments'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                  : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
              } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px`}
            >
              Attachments
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`${
                activeTab === 'activities'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                  : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
              } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px`}
            >
              Activities
            </button>
            <button
              onClick={() => setActiveTab('related')}
              className={`${
                activeTab === 'related'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                  : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
              } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px`}
            >
              Related Records
            </button>
            <button
              onClick={() => setActiveTab('closure')}
              className={`${
                activeTab === 'closure'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-gray-50'
                  : 'border-b-2 border-transparent text-gray-600 hover:text-gray-800'
              } whitespace-nowrap py-3 px-6 font-medium text-sm transition-all -mb-px flex items-center`}
            >
              Closure
              {(incident.state === 'resolved' || incident.state === 'closed' || incident.state === 'cancelled') && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {incident.state === 'cancelled' ? '!' : '✓'}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          /* Incident Details */
          <div className="bg-white shadow-sm rounded-b-lg border border-t-0 border-gray-200">
          <form className="p-6">
            <div className="flex justify-end mb-4">
              {!isEditing && (canEdit || canEditAssignment) && (
                <button
                  onClick={handleEdit}
                  type="button"
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title={editAccessReason}
                >
                  <PencilIcon className="h-4 w-4 mr-1.5" />
                  Edit
                </button>
              )}
            </div>
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {isEditing && !isAssignmentOnlyEditMode ? (
              <div>
                {/* Row 1: Number and Opened */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Number
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={incident.number}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Opened
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={formatDateTime(incident.createdAt)}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Impact and Urgency */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Impact
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.impact || 'low_3'}
                          onChange={(value) => {
                            const newImpact = value || 'low_3';
                            const newPriority = calculatePriorityFromImpactUrgency(newImpact, editForm.urgency || 'low_3');
                            setEditForm({ ...editForm, impact: newImpact, priority: newPriority });
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(value: string) => {
                                const option = impactOptions.find(o => o.value === value);
                                return option ? option.label : '';
                              }}
                              onChange={(e) => setImpactQuery(e.target.value)}
                              placeholder="Select Impact"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setImpactQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {impactOptions
                                  .filter(option => option.label.toLowerCase().includes(impactQuery.toLowerCase()))
                                  .map((option) => (
                                    <Combobox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {option.label}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Urgency
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.urgency || 'low_3'}
                          onChange={(value) => {
                            const newUrgency = value || 'low_3';
                            const newPriority = calculatePriorityFromImpactUrgency(editForm.impact || 'low_3', newUrgency);
                            setEditForm({ ...editForm, urgency: newUrgency, priority: newPriority });
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(value: string) => {
                                const option = urgencyOptions.find(o => o.value === value);
                                return option ? option.label : '';
                              }}
                              onChange={(e) => setUrgencyQuery(e.target.value)}
                              placeholder="Select Urgency"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setUrgencyQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {urgencyOptions
                                  .filter(option => option.label.toLowerCase().includes(urgencyQuery.toLowerCase()))
                                  .map((option) => (
                                    <Combobox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {option.label}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Priority and State */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={priorityOptions.find(o => o.value === editForm.priority)?.label || ''}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={getStateLabel(incident.state)}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 capitalize"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Channel and Category */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Channel
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.channel || 'web'}
                          onChange={(value) => setEditForm({ ...editForm, channel: value || 'web' })}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(value: string) => {
                                const option = channelOptions.find(o => o.value === value);
                                return option ? option.label : '';
                              }}
                              onChange={(e) => setChannelQuery(e.target.value)}
                              placeholder="Select Channel"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setChannelQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {channelOptions
                                  .filter(option => option.label.toLowerCase().includes(channelQuery.toLowerCase()))
                                  .map((option) => (
                                    <Combobox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {option.label}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.category || 'issue'}
                          onChange={(value) => setEditForm({ ...editForm, category: value || 'issue' })}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(value: string) => {
                                const option = categoryOptions.find(o => o.value === value);
                                return option ? option.label : '';
                              }}
                              onChange={(e) => setCategoryQuery(e.target.value)}
                              placeholder="Select Category"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setCategoryQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {categoryOptions
                                  .filter(option => option.label.toLowerCase().includes(categoryQuery.toLowerCase()))
                                  .map((option) => (
                                    <Combobox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {option.label}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Customer and Engagement */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Customer
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={incident.Customer ? incident.Customer.accountName : 'Not specified'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Engagement
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={incident.Engagement ? incident.Engagement.name : 'Not specified'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 5: Contact and Service Plan */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.contactId || ''}
                          onChange={(value) => setEditForm({ ...editForm, contactId: value || null })}
                          disabled={!editForm.customerId && !incident?.customerId}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                              displayValue={(contactId: string) => {
                                const contact = (contactsData?.users || []).find((c: any) => c.id === contactId);
                                return contact ? `${contact.firstName} ${contact.lastName}` : '';
                              }}
                              onChange={(e) => setContactQuery(e.target.value)}
                              placeholder={editForm.customerId || incident?.customerId ? "Search contact by name or email..." : "Select Customer first"}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setContactQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <Combobox.Option
                                  value=""
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-500'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className="block truncate italic">None</span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}>
                                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                                {(contactsData?.users || [])
                                  .filter((contact: any) => {
                                    if (!contactQuery) return true;
                                    const searchLower = contactQuery.toLowerCase();
                                    const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
                                    const email = (contact.email || '').toLowerCase();
                                    return fullName.includes(searchLower) || email.includes(searchLower);
                                  })
                                  .map((contact: any) => (
                                    <Combobox.Option
                                      key={contact.id}
                                      value={contact.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <div className="flex flex-col">
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {contact.firstName} {contact.lastName}
                                            </span>
                                            <span className={`block truncate text-xs ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                              {contact.email}
                                            </span>
                                          </div>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service Plan
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.servicePlanId || ''}
                          onChange={(value) => setEditForm({ ...editForm, servicePlanId: value || null })}
                          disabled={!editForm.engagementId}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                              displayValue={(servicePlanId: string) => {
                                const plan = (servicePlansData?.plans || servicePlansData?.servicePlans || []).find((p: any) => p.id === servicePlanId);
                                return plan ? plan.name : '';
                              }}
                              onChange={(e) => setServicePlanQuery(e.target.value)}
                              placeholder={editForm.engagementId ? "Select Service Plan" : "Select Engagement first"}
                              disabled={!editForm.engagementId}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={!editForm.engagementId}>
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setServicePlanQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(servicePlansData?.plans || servicePlansData?.servicePlans || [])
                                  .filter((plan: any) => plan && plan.id && plan.name && plan.name.toLowerCase().includes(servicePlanQuery.toLowerCase()))
                                  .map((plan: any) => (
                                    <Combobox.Option
                                      key={plan.id}
                                      value={plan.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {plan.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 6: Resource and Resource ID / Custom Resources */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Resource
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={
                            incident.Resource
                              ? incident.Resource.name
                              : incident.otherResourceNames
                                ? 'Others (see Custom Resources)'
                                : 'Not specified'
                          }
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {incident.otherResourceNames && !incident.Resource ? 'Custom Resources' : 'Resource ID'}
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={
                            incident.otherResourceNames && !incident.Resource
                              ? incident.otherResourceNames
                              : (incident.resourceIdValue || incident.Resource?.resourceId || '')
                          }
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 truncate"
                          placeholder="Not specified"
                          title={
                            incident.otherResourceNames && !incident.Resource
                              ? incident.otherResourceNames
                              : (incident.resourceIdValue || incident.Resource?.resourceId || '')
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reference Alert (if incident was created from alert) */}
                {incident.ReferenceAlert && (
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reference Alert
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Link
                            to={`/alerts/${incident.ReferenceAlert.id}`}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                          >
                            <TicketIcon className="h-4 w-4 mr-1" />
                            {incident.ReferenceAlert.number}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Row 7: Assignment Group and Assigned To */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignmentGroupId || ''}
                          onChange={(value) => {
                            const newGroupId = value || null;
                            setEditForm({ ...editForm, assignmentGroupId: newGroupId, assignedTo: null });
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(groupId: string) => {
                                const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                                return group ? group.name : '';
                              }}
                              onChange={(e) => setAssignmentGroupQuery(e.target.value)}
                              placeholder="Select Assignment Group"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignmentGroupQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(assignmentGroupsData || [])
                                  .filter((group: any) => group && group.id && group.name && group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                                  .map((group: any) => (
                                    <Combobox.Option
                                      key={group.id}
                                      value={group.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {group.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assigned To
                        </label>
                      </div>
                      <div className="col-span-9">
                        <SearchableUserDropdown
                          value={editForm.assignedTo || null}
                          onChange={(value) => {
                            setEditForm({ ...editForm, assignedTo: value });
                            // Clear error when field is filled
                            if (value && errors.assignedTo) {
                              setErrors(prev => ({ ...prev, assignedTo: '' }));
                            }
                          }}
                          placeholder={editForm.assignmentGroupId ? "Select Assignee from Group" : "Select Assignment Group First"}
                          disabled={!editForm.assignmentGroupId}
                          className="h-8"
                          users={groupMembersData || []}
                        />
                        {errors.assignedTo && (
                          <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                        )}
                        {!editForm.assignmentGroupId && (
                          <p className="mt-1 text-sm text-gray-500">Please select an assignment group first</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 mb-6">
                  {/* Short Description - Full width */}
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                          <span className="text-red-500">*</span> Short Description
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.shortDescription || ''}
                          onChange={(value) => setEditForm({ ...editForm, shortDescription: value })}
                          placeholder="Brief summary of the incident"
                          minHeight="80px"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 mb-8">
                  {/* Description - Full width */}
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                          Description
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.description || ''}
                          onChange={(value) => setEditForm({ ...editForm, description: value })}
                          placeholder="Enter incident description"
                          minHeight="120px"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {(editForm.state === 'resolved' || editForm.state === 'closed') && (
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-12">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                            Resolution Notes
                          </label>
                        </div>
                        <div className="col-span-10">
                          <RichTextEditor
                            value={editForm.resolutionNotes || ''}
                            onChange={(value) => setEditForm({ ...editForm, resolutionNotes: value })}
                            placeholder="Describe how the incident was resolved"
                            minHeight="120px"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={updateIncidentMutation.isPending}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateIncidentMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {isEditing && isAssignmentOnlyEditMode && (
                  <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    You can update Assignment Group and Assigned To for this ticket. Other fields are read-only.
                  </div>
                )}
                {/* Row 1: Number and Opened */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Number
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.number}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Opened
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {formatDateTime(incident.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Impact and Urgency */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Impact
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {impactOptions.find(o => o.value === incident.impact)?.label || '3 - Low'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Urgency
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {urgencyOptions.find(o => o.value === incident.urgency)?.label || '3 - Low'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Priority and State */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${priorityClass}`}>
                            {priorityText}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center capitalize">
                          {getDisplayLabel(incident.state, caseStateLabels)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Channel and Category */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Channel
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center capitalize">
                          {incident.channel || 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center capitalize">
                          {incident.category || 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Customer and Engagement */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Customer
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.Customer ? incident.Customer.accountName : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Engagement
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.Engagement ? incident.Engagement.name : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 5: Contact and Service Plan */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.contact ? formatUserName(incident.contact as any) : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service Plan
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.ServicePlan ? incident.ServicePlan.name : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 6: Resource and Resource ID / Custom Resources */}
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Resource
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.Resource
                            ? incident.Resource.name
                            : incident.otherResourceNames
                              ? 'Others (see Custom Resources)'
                              : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {incident.otherResourceNames && !incident.Resource ? 'Custom Resources' : 'Resource ID'}
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center overflow-hidden"
                          title={
                            incident.otherResourceNames && !incident.Resource
                              ? incident.otherResourceNames
                              : (incident.resourceIdValue || incident.Resource?.resourceId || '')
                          }
                        >
                          <span className="truncate">
                            {incident.otherResourceNames && !incident.Resource
                              ? incident.otherResourceNames
                              : (incident.resourceIdValue || incident.Resource?.resourceId || 'Not specified')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reference Alert (if incident was created from alert) */}
                {incident.ReferenceAlert && (
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reference Alert
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Link
                            to={`/alerts/${incident.ReferenceAlert.id}`}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                          >
                            <TicketIcon className="h-4 w-4 mr-1" />
                            {incident.ReferenceAlert.number}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Row 7: Assignment Group and Assigned To */}
                {isEditing && isAssignmentOnlyEditMode ? (
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignmentGroupId || ''}
                          onChange={(value) => {
                            const newGroupId = value || null;
                            setEditForm({ ...editForm, assignmentGroupId: newGroupId, assignedTo: null });
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(groupId: string) => {
                                const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                                return group ? group.name : '';
                              }}
                              onChange={(e) => setAssignmentGroupQuery(e.target.value)}
                              placeholder="Select Assignment Group"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignmentGroupQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(assignmentGroupsData || [])
                                  .filter((group: any) => group && group.id && group.name && group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                                  .map((group: any) => (
                                    <Combobox.Option
                                      key={group.id}
                                      value={group.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {group.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assigned To
                        </label>
                      </div>
                      <div className="col-span-9">
                        <SearchableUserDropdown
                          value={editForm.assignedTo || null}
                          onChange={(value) => {
                            setEditForm({ ...editForm, assignedTo: value });
                            if (value && errors.assignedTo) {
                              setErrors(prev => ({ ...prev, assignedTo: '' }));
                            }
                          }}
                          placeholder={editForm.assignmentGroupId ? "Select Assignee from Group" : "Select Assignment Group First"}
                          disabled={!editForm.assignmentGroupId}
                          className="h-8"
                          users={groupMembersData || []}
                        />
                        {errors.assignedTo && (
                          <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ) : (
                <div className="grid grid-cols-12 gap-6 mb-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                          {incident.AssignmentGroup ? incident.AssignmentGroup.name : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assigned To
                        </label>
                      </div>
                      <div className="col-span-9 flex gap-2">
                        <input
                          type="text"
                          value={incident.assignedTo ? formatUserName(incident.assignedTo as any) : 'Unassigned'}
                          readOnly
                          className="flex-1 px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                        {shouldShowAssignToMeButton() && (
                          <button
                            onClick={handleAssignToMe}
                            disabled={updateIncidentMutation.isPending}
                            className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 whitespace-nowrap h-8"
                          >
                            {updateIncidentMutation.isPending ? 'Assigning...' : 'Assign To Me'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                <div className="grid grid-cols-12 gap-6 mb-6">
                  {/* Short Description - Full width */}
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                          Short Description
                        </label>
                      </div>
                      <div className="col-span-10">
                        <ResizableTextDisplay
                          content={incident.shortDescription || 'No short description provided'}
                          defaultHeight={60}
                          minHeight={40}
                          maxHeight={300}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 mb-8">
                  {/* Description - Full width */}
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                          Description
                        </label>
                      </div>
                      <div className="col-span-10">
                        <ResizableTextDisplay
                          content={incident.description || 'No description provided'}
                          defaultHeight={100}
                          minHeight={60}
                          maxHeight={500}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {incident.ParentCase && (
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-12">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                            Parent Case
                          </label>
                        </div>
                        <div className="col-span-10">
                          <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm h-8 flex items-center">
                            <span className="font-medium">{incident.ParentCase.number}</span> - {stripHtml(incident.ParentCase.shortDescription)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {getIncidentResolutionNotes(incident) && (
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-12">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                            Resolution Notes
                          </label>
                        </div>
                        <div className="col-span-10">
                          <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm min-h-[4rem]">
                            <RichTextDisplay content={getIncidentResolutionNotes(incident)} />
                            {incident.resolutionCode && (
                              <div className="mt-1 text-gray-500">Code: {incident.resolutionCode}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isEditing && isAssignmentOnlyEditMode && (
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={updateIncidentMutation.isPending}
                      className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {updateIncidentMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
        )}

        {activeTab === 'notes' && (
          /* Work Notes Tab */
          <div className="bg-white shadow-sm rounded-b-lg border border-t-0 border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              Work Notes & Communication
            </h2>
          </div>

          <div className="p-6">
            {/* Add Note Form */}
            {canAddWorkNote && (
              <form onSubmit={handleAddNote} className="mb-6">
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">Add Note</label>
                  <RichTextEditor
                    value={newNote}
                    onChange={(value) => setNewNote(value)}
                    placeholder="Enter your note..."
                    minHeight="120px"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="work_note"
                        checked={noteType === 'work_note'}
                        onChange={(e) => setNoteType(e.target.value as 'work_note')}
                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Work Note</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="customer_note"
                        checked={noteType === 'customer_note'}
                        onChange={(e) => setNoteType(e.target.value as 'customer_note')}
                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Customer Note</span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={!newNote.trim() || addNoteMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {addNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </form>
            )}

            {/* Notes List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Work Notes</h3>
              {isLoadingNotes ? (
                <TabLoadingSpinner label="Loading work notes" />
              ) : workNotes && workNotes.length > 0 ? (
                <div className="space-y-4">
                  {workNotes.map((note: WorkNote) => (
                    <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {note.User?.firstName?.charAt(0).toUpperCase() || 'U'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              {note.User ? `${note.User.firstName} ${note.User.lastName}` : 'Unknown User'}
                            </span>
                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              note.isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {note.isPublic ? 'Public' : 'Internal'}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(note.timestamp)}
                        </span>
                      </div>
                      <div className="ml-11">
                        <div className="text-sm text-gray-700">
                          <RichTextDisplay content={note.note} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No work notes yet</p>
                  <p className="mt-1 text-xs text-gray-400">Work notes will appear here once added</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {activeTab === 'attachments' && (
          <div className="bg-white shadow rounded-lg p-6">
            <AttachmentTab
              entityType="case"
              entityId={id!}
              canUpload={canAddAttachments}
              canDelete={canAddAttachments}
              ticketState={incident?.state}
              onAttachmentAdded={() => {
                if (user && incident) {
                  trackIncidentOperation(
                    'Incident Details - Edit',
                    user.email || user.firstName || 'Unknown',
                    {
                      action: 'Added Attachments',
                      incidentNumber: incident.number || id || 'Unknown',
                    },
                    {
                      firstName: user.firstName,
                      lastName: user.lastName,
                      role: user.role,
                      roleType: user.roleType,
                      roleLevel: user.roleLevel,
                      userClass: user.class,
                      customerId: user.customerId,
                      customerName: user.customerName,
                      engagementId: user.engagementId,
                      engagementName: user.engagementName,
                      isServiceDeskUser: isServiceDeskUser,
                    }
                  );
                }
              }}
              onAttachmentDeleted={() => {
                if (user && incident) {
                  trackIncidentOperation(
                    'Incident Details - Edit',
                    user.email || user.firstName || 'Unknown',
                    {
                      action: 'Removed Attachments',
                      incidentNumber: incident.number || id || 'Unknown',
                    },
                    {
                      firstName: user.firstName,
                      lastName: user.lastName,
                      role: user.role,
                      roleType: user.roleType,
                      roleLevel: user.roleLevel,
                      userClass: user.class,
                      customerId: user.customerId,
                      customerName: user.customerName,
                      engagementId: user.engagementId,
                      engagementName: user.engagementName,
                      isServiceDeskUser: isServiceDeskUser,
                    }
                  );
                }
              }}
            />
          </div>
        )}

        {activeTab === 'activities' && (
          <div className="bg-white shadow rounded-lg p-6">
            <ActivityTab entityId={id!} />
          </div>
        )}

        {activeTab === 'related' && (
          <div className="bg-white shadow rounded-lg p-6">
            <RelatedRecordsTab
              caseId={id!}
              caseNumber={incident?.number || ''}
              ticketType="INCIDENT"
              canManage={canAddRelated}
              onRelatedRecordAdded={() => {
                if (user && incident) {
                  trackIncidentOperation(
                    'Incident Details - Edit',
                    user.email || user.firstName || 'Unknown',
                    {
                      action: 'Added Related Record',
                      incidentNumber: incident.number || id || 'Unknown',
                    },
                    {
                      firstName: user.firstName,
                      lastName: user.lastName,
                      role: user.role,
                      roleType: user.roleType,
                      roleLevel: user.roleLevel,
                      userClass: user.class,
                      customerId: user.customerId,
                      customerName: user.customerName,
                      engagementId: user.engagementId,
                      engagementName: user.engagementName,
                      isServiceDeskUser: isServiceDeskUser,
                    }
                  );
                }
              }}
              onRelatedRecordDeleted={() => {
                if (user && incident) {
                  trackIncidentOperation(
                    'Incident Details - Edit',
                    user.email || user.firstName || 'Unknown',
                    {
                      action: 'Removed Related Record',
                      incidentNumber: incident.number || id || 'Unknown',
                    },
                    {
                      firstName: user.firstName,
                      lastName: user.lastName,
                      role: user.role,
                      roleType: user.roleType,
                      roleLevel: user.roleLevel,
                      userClass: user.class,
                      customerId: user.customerId,
                      customerName: user.customerName,
                      engagementId: user.engagementId,
                      engagementName: user.engagementName,
                      isServiceDeskUser: isServiceDeskUser,
                    }
                  );
                }
              }}
            />
          </div>
        )}

        {activeTab === 'closure' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-3">Closure Information</h3>

              {/* Resolution Info Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Resolved By</label>
                  <p className="text-sm text-gray-900">
                    {incident.resolvedBy
                      ? `${incident.resolvedBy.firstName || ''} ${incident.resolvedBy.lastName || ''}`.trim() || '-'
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Resolved On</label>
                  <p className="text-sm text-gray-900">
                    {incident.resolvedAt ? formatDateTime(incident.resolvedAt) : '-'}
                  </p>
                </div>
              </div>

              {/* Closure Info Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Closed By</label>
                  <p className="text-sm text-gray-900">
                    {incident.closedBy
                      ? `${incident.closedBy.firstName || ''} ${incident.closedBy.lastName || ''}`.trim() || '-'
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Closed On</label>
                  <p className="text-sm text-gray-900">
                    {incident.closedAt ? formatDateTime(incident.closedAt) : '-'}
                  </p>
                </div>
              </div>

              {/* Resolution Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resolution Notes
                </label>
                {getIncidentResolutionNotes(incident) ? (
                  <div className="w-full px-4 py-3 border border-green-300 rounded-lg bg-green-50 text-sm">
                    <RichTextDisplay content={getIncidentResolutionNotes(incident)} />
                  </div>
                ) : (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                    No resolution notes provided
                  </div>
                )}
                {incident.resolutionCode && (
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-medium">Resolution Code:</span> {incident.resolutionCode}
                  </div>
                )}
              </div>

              {/* Closure Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Closure Notes
                </label>
                {incident.closureNotes ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm">
                    <RichTextDisplay content={incident.closureNotes} />
                  </div>
                ) : (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                    No closure notes provided
                  </div>
                )}
              </div>

              {/* Cancellation Reason (if cancelled) */}
              {incident.state === 'cancelled' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cancellation Reason
                  </label>
                  {incident.cancellationReason ? (
                    <div className="w-full px-4 py-3 border border-red-300 rounded-lg bg-red-50 text-sm">
                      <RichTextDisplay content={incident.cancellationReason} />
                    </div>
                  ) : (
                    <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                      No cancellation reason provided
                    </div>
                  )}
                  {incident.cancelledAt && (
                    <div className="mt-1 text-xs text-gray-500">
                      Cancelled on: {formatDateTime(incident.cancelledAt)}
                    </div>
                  )}
                </div>
              )}

              {/* Summary Section */}
              {(incident.state === 'resolved' || incident.state === 'closed' || incident.state === 'cancelled') && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        incident.state === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {incident.state === 'resolved' ? 'Resolved' :
                         incident.state === 'closed' ? 'Closed' : 'Cancelled'}
                      </span>
                    </div>
                    {incident.state === 'resolved' && canChangeState && (
                      <button
                        onClick={() => setActiveTab('details')}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        View workflow to close or reopen
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Empty State when no closure info */}
              {incident.state !== 'resolved' && incident.state !== 'closed' && incident.state !== 'cancelled' && (
                <div className="text-center py-8 text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm">This incident has not been resolved or closed yet.</p>
                  <p className="mt-1 text-xs text-gray-400">Closure information will appear here once the incident is resolved.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmStateChange}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmButtonClass={confirmModal.confirmButtonClass}
          isLoading={updateIncidentMutation.isPending}
        />

        {/* On-Hold Reason Modal */}
        <OnHoldReasonModal
          isOpen={onHoldModal.isOpen}
          onClose={handleCloseOnHoldModal}
          onConfirm={handleConfirmOnHold}
          title={onHoldModal.title}
          message={
            serviceNowHoldReasonOptions.length > 0
              ? 'Select the ServiceNow on hold reason for this incident:'
              : onHoldModal.message
          }
          confirmLabel="Put On Hold"
          confirmButtonClass="bg-yellow-600 hover:bg-yellow-700 text-white"
          isLoading={updateIncidentMutation.isPending}
          holdReasonOptions={serviceNowHoldReasonOptions}
        />

        {/* Resolution Notes Modal */}
        <ResolutionNotesModal
          isOpen={resolutionModal.isOpen}
          onClose={handleCloseResolutionModal}
          onConfirm={handleConfirmResolution}
          title={resolutionModal.title}
          message={resolutionModal.message}
          confirmLabel="Mark as Resolved"
          confirmButtonClass="bg-green-600 hover:bg-green-700 text-white"
          isLoading={updateIncidentMutation.isPending}
        />

        {/* Cancellation Reason Modal */}
        <CancellationReasonModal
          isOpen={cancellationModal.isOpen}
          onClose={handleCloseCancellationModal}
          onConfirm={handleConfirmCancellation}
          title={cancellationModal.title}
          message={cancellationModal.message}
          confirmLabel="Cancel Incident"
          confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
          isLoading={updateIncidentMutation.isPending}
        />

        {/* Reopen Reason Modal */}
        <ReopenReasonModal
          isOpen={reopenModal.isOpen}
          onClose={handleCloseReopenModal}
          onConfirm={handleConfirmReopen}
          title={reopenModal.title}
          message={reopenModal.message}
          confirmLabel="Reopen Incident"
          confirmButtonClass="bg-blue-600 hover:bg-blue-700 text-white"
          isLoading={updateIncidentMutation.isPending}
        />

        <ClosureNotesModal
          isOpen={closureModal.isOpen}
          onClose={handleCloseClosureModal}
          onConfirm={handleConfirmClosure}
          title={closureModal.title}
          message={closureModal.message}
          confirmLabel="Close Incident"
          confirmButtonClass="bg-gray-600 hover:bg-gray-700 text-white"
          isLoading={updateIncidentMutation.isPending}
        />

        {/* Breach Reason Modal */}
        <BreachReasonModal
          isOpen={breachReasonModal.isOpen}
          onClose={() => setBreachReasonModal({ isOpen: false, targetType: undefined })}
          onConfirm={(reason, targetType) => {
            if (targetType) {
              breachReasonMutation.mutate({ breachReason: reason, targetType });
            }
          }}
          targetType={breachReasonModal.targetType}
          isLoading={breachReasonMutation.isPending}
        />

        {/* Survey Confirmation Modal (for moderate/low priority) */}
        <SurveyConfirmationModal
          isOpen={surveyConfirmModal.isOpen}
          onSendSurvey={handleSurveyConfirmYes}
          onDontSend={handleSurveyConfirmNo}
          title="Send Survey to Customer"
          message="Do you want to send the survey to the customer?"
          isLoading={false}
          defaultToggleOn={false}
        />

        {/* Survey Result Modal (for success/error messages) */}
        <Transition appear show={surveyResultModal.isOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setSurveyResultModal({ isOpen: false, type: 'success', message: '' })}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {surveyResultModal.type === 'success' ? (
                            <CheckCircleIcon className="h-6 w-6 text-green-600" />
                          ) : (
                            <XCircleIcon className="h-6 w-6 text-red-600" />
                          )}
                        </div>
                        <div>
                          <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                            {surveyResultModal.type === 'success' ? 'Success' : 'Error'}
                          </Dialog.Title>
                        </div>
                      </div>
                      <button
                        onClick={() => setSurveyResultModal({ isOpen: false, type: 'success', message: '' })}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="mt-2 ml-9">
                      <p className="text-sm text-gray-500">
                        {surveyResultModal.message}
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setSurveyResultModal({ isOpen: false, type: 'success', message: '' })}
                        className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                          surveyResultModal.type === 'success'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        OK
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Duplicate Incident Modal */}
        <DuplicateIncidentModal
          isOpen={duplicateModalOpen}
          onClose={() => setDuplicateModalOpen(false)}
          incident={incident}
          onSuccess={(newIncidentNumber) => {
            if (user && incident) {
              trackIncidentOperation(
                'Incident Created',
                user.email || user.firstName || 'Unknown',
                {
                  action: 'Duplicate Incident is created',
                  incidentNumber: incident.number || id || 'Unknown',
                  newIncidentNumber,
                },
                {
                  firstName: user.firstName,
                  lastName: user.lastName,
                  role: user.role,
                  roleType: user.roleType,
                  roleLevel: user.roleLevel,
                  userClass: user.class,
                  customerId: user.customerId,
                  customerName: user.customerName,
                  engagementId: user.engagementId,
                  engagementName: user.engagementName,
                  isServiceDeskUser: isServiceDeskUser,
                }
              );
            }
          }}
        />

        {/* Create Problem from Incident Modal */}
        <CreateProblemFromIncidentModal
          isOpen={createProblemModalOpen}
          onClose={() => setCreateProblemModalOpen(false)}
          incident={incident}
          onSuccess={(problemNumber, incidentNumber) => {
            if (user) {
              trackIncidentOperation(
                'Problem Created on Incident',
                user.email || user.firstName || 'Unknown',
                {
                  action: 'Problem Created',
                  incidentNumber: incidentNumber || id || 'Unknown',
                  problemNumber,
                },
                {
                  firstName: user.firstName,
                  lastName: user.lastName,
                  role: user.role,
                  roleType: user.roleType,
                  roleLevel: user.roleLevel,
                  userClass: user.class,
                  customerId: user.customerId,
                  customerName: user.customerName,
                  engagementId: user.engagementId,
                  engagementName: user.engagementName,
                  isServiceDeskUser: isServiceDeskUser,
                }
              );
            }
          }}
        />

        {/* Record Time Modal */}
        {incident && incident.Customer && incident.Engagement && incident.AssignmentGroup && (
          <RecordTimeModal
            isOpen={recordTimeModalOpen}
            onClose={() => setRecordTimeModalOpen(false)}
            onSuccess={() => {
              // Optionally refresh the incident data after recording time
              queryClient.invalidateQueries({ queryKey: ['incident', id] });
            }}
            customerName={incident.Customer.accountName}
            customerId={incident.customerId || incident.Customer.id}
            engagementName={incident.Engagement.name}
            engagementId={incident.engagementId || incident.Engagement.id}
            groupName={incident.AssignmentGroup.name}
            groupId={incident.assignmentGroupId || incident.AssignmentGroup.id}
            ticketNumber={incident.number || ''}
          />
        )}

        {/* Assignment Group Change Confirmation Modal */}
        <AssignmentGroupChangeConfirmModal
          isOpen={groupChangeConfirmModal.isOpen}
          onConfirm={handleConfirmGroupChange}
          onCancel={handleCancelGroupChange}
          newGroupName={groupChangeConfirmModal.newGroupName}
          ticketNumber={incident?.number}
        />
      </div>
    </div>
  );
};

export default IncidentDetail; 